import React, { useCallback } from 'react';
import { Modal, Text, TextInput, Button, PhoneInput, SelectInput } from '@/ui-kits';
import { useFormik } from 'formik';
import { InviteHospitalSpecialistRequestPayload } from '@/interface';
import { InviteHospitalSpecialistFormValidation } from '@/utils/schema';
import { hospitalPocProfileSpecializationsSelector, hospitalPocSpecialistInviteLoadingSelector } from '@/services/redux/selectors';
import { useSelector } from 'react-redux';
import { createFormattedBlurHandler } from '@/utils/common_functions';

interface InviteHospitalSpecialistModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: InviteHospitalSpecialistRequestPayload) => void;
}

const InviteHospitalSpecialistFormInitialValues: InviteHospitalSpecialistRequestPayload = {
  name: '',
  email: '',
  phone: '',
  specialization: null as any,
};
export const InviteHospitalSpecialistModal: React.FC<InviteHospitalSpecialistModalProps> = props => {
  const { open, onClose, onSave } = props;

  // hooks
  const { values, errors, touched, handleBlur, handleSubmit, handleChange, setFieldValue, isValid, dirty } = useFormik<InviteHospitalSpecialistRequestPayload>({
    initialValues: InviteHospitalSpecialistFormInitialValues,
    validationSchema: InviteHospitalSpecialistFormValidation,
    onSubmit: onSave,
  });

  // selectors
  const specializations = useSelector(hospitalPocProfileSpecializationsSelector);
  const isloading = useSelector(hospitalPocSpecialistInviteLoadingSelector);

  // Create formatted blur handler for name field to capitalize first letters
  const handleNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'name');

  const closeModal = useCallback(() => {
    if (!dirty) {
      onClose();
    }
  }, [dirty]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="relative backdrop:bg-[#0000007e] bg-white py-4 sm:py-6 px-4 sm:px-6 md:px-8 rounded-xl gap-4 w-[90vw] max-w-[500px] shadow-lg">
      <Text variant="heading" className="text-ui_blue">
        Add Specialist
      </Text>

      <div className="flex flex-col gap-2 sm:gap-4 my-5">
        {/* Admin Name */}
        <TextInput
          name="name"
          label="Full Name"
          value={values.name}
          onChangeText={handleChange('name')}
          onBlur={handleNameBlur as any}
          error={errors.name}
          touched={touched.name}
          required
          preventLeadingSpace
          preventTrailingSpace
          placeholder="Enter specialist name"
          wrapperClassName="border w-full"
        />

        {/* Email Address */}
        <TextInput
          name="email"
          label="Email Address"
          value={values.email}
          onChangeText={handleChange('email')}
          onBlur={handleBlur('email')}
          error={errors.email}
          touched={touched.email}
          required
          placeholder="Enter specialist email ID"
          wrapperClassName="border w-full"
        />

        {/* Phone Number */}
        <PhoneInput
          name="phone"
          label="Phone"
          value={values.phone}
          onChange={phone => setFieldValue('phone', phone)}
          onBlur={handleBlur('phone')}
          error={errors.phone}
          touched={touched.phone}
          required
          placeholder="Enter specialist phone no."
          wrapperClassName="border w-full"
        />

        <SelectInput
          name="specialization"
          label="Status"
          value={values.specialization}
          onChange={item => setFieldValue('specialization', item.id)}
          onBlur={handleBlur('specialization')}
          error={errors.specialization}
          touched={touched.specialization}
          required
          placeholder="Select specialization"
          wrapperClassName="border w-full"
          options={specializations}
          dropdownPosition="above"
        />

        {/* Action Buttons */}
      </div>
      <div className="flex gap-4 items-center justify-center">
        <Button className="w-full max-w-[130px] py-2" variant="secondary" title="Cancel" onClick={onClose} />
        <Button className="w-full max-w-[130px] py-2" variant="primary" isLoading={isloading} title="Save" onClick={() => handleSubmit()} disabled={!isValid || !dirty} />
      </div>
    </Modal>
  );
};
